<template>
  <div class="city-tour-page">
    <CityBanner :imageurl="cityData.cityBannerImage" :label="getCityName()" :sub-title="cityData.keyword" />
    <div class="city-container">
      <div class="top-func">
        <div class="mobile-top">
          <h2 class="header-2">{{ cityTitle }}</h2>
          <div>
            <TourFilterMobile
              :list="filterNames"
              :tourList="sortedTours"
            />
            <SortersDropDown 
              :options="optionsDropdown"
              :tourList="sortedTours"
            />
          </div>
        </div>
        <div class="desktop-top">
          <div class="first-line">
            <h2 class="header-2">{{ cityTitle }}</h2>
            <Dropdown
              :defaultValue="curSorter"
              :options="optionsDropdown" 
              :callback="dropdownChange" 
              :tourList="sortedTours"
            >
              <img 
                slot="icon" 
                class="thumbnail-icon icon lazyload" 
                width="10px" 
                height="10px" 
                :data-src="ArrowIcon" 
              />
            </Dropdown>
          </div>
          
          <CategoryFilter :categories="[{ filterName: 'All' }, ...cityData.filtersWithIcon]" />
        </div>
        
      </div>
      <div class="city-tour-list city-section">
        <TourListContainer :tourList="sortedTours"/>
      </div>
      <GuideFaq class="city-section" />
      <BlogSlider class="city-section" />
      <LocationSlider class="city-section" />
    </div>
  </div>
</template>

<script>

import ArrowIcon from '~/assets/svg/arrow-red-down.svg'
import { mapGetters } from 'vuex'
import CityBanner from '~/components/utils/CityBanner'
import TourFilterMobile from '~/components/containers/citypage/TourFilterMobile'
import Dropdown from '~/components/utils/Dropdown'
import SortersDropDown from '~/components/utils/SortersDropDown'
import TourListContainer from '~/components/containers/citypage/TourListContainer'
import CategoryFilter from '~/components/containers/citypage/CategoryFilter'
import GuideFaq from '~/components/containers/citypage/GuideFaq'
import BlogSlider from '@/components/containers/citypage/BlogSlider'
import LocationSlider from '@/components/containers/citypage/LocationSlider'

import setRobots from '~/mixins/setRobots.js'
import promoMixin from  '~/mixins/promoMixin.js'
import imageOptimo from '~/mixins/imageOptimoMixin.js'

export default {
  name: 'CityPage',
  components: {
    LocationSlider,
    BlogSlider,
    GuideFaq,
    SortersDropDown,
    TourFilterMobile,
    CategoryFilter,
    CityBanner,
    TourListContainer,
    Dropdown
  },
  mixins: [setRobots,promoMixin, imageOptimo],
  computed: {
    ...mapGetters([
      'curSorter',
      'tags',
      'microData',
      'cityData',
      'cityTours',
      'tourfilter',
      'screenWidth'
    ]),
    filterNames () {
      const filters = this.cityData.filtersWithIcon?.map(item => item.filterName)
      return filters
    },
    cityTitle () {
      const cityName = this.getCityName().replace('Tours', '').trim();
      if (this.screenWidth > 1024) {
        return `View our tours in ${cityName}`
      } else {
        return `Our tours in ${cityName}`
      }
    },
    citySubTitle () {
      const keyword = this.cityData.keyword
      if (this.screenWidth > 1024) {
        return keyword
      } else {
        return null
      }
    },
    tourList () {
      // remove dup should be done in backend
      const tourArray = this.cityTours
      
      // if no filter selected, return all.
      if (!this.tourfilter?.length) {
        return tourArray
      }
      // if more than 0 filter selected, return only tour with a tag matching
      const matchFilter = tourArray.filter(tour => {
        let match = false
        this.tourfilter && this.tourfilter.forEach(filter => (tour.tourFilterTags.indexOf(filter) > -1) && (match = tour))
        return match
      }, this)
      // if no match found, reset default
      if (!matchFilter.length) {
        this.initialSorting = { label: 'most popular', value: 'popular' }
        return tourArray
      }
      // return all tour having a match
      return matchFilter
    },
    sortedTours () {
      const unavailableFlag = 'Currently Unavailable'

      let tours = this.tourList.slice(0).map(i => ({ ...i, unavailable: (i?.flag === unavailableFlag) }))

      switch (this.curSorter.value) {
        case 'lowest':
          tours.sort((a, b) => this.getAnchorPriceNum(a) - this.getAnchorPriceNum(b))
          break
        case 'highest':
          tours.sort((a, b) => this.getAnchorPriceNum(b) - this.getAnchorPriceNum(a))
          break
        case 'rating':
          // use # of reviews
          tours.sort((a, b) => this.getPopularity(b) - this.getPopularity(a))
          break
        case 'popular':
          break
        default:
          break
      }
      // filter out unavailable tours and put them to the last
    
      const availableTours = tours.filter(tour => tour?.flag !== unavailableFlag)
      const unavailableTours = tours.filter(tour => tour?.flag === unavailableFlag)
      // console.log('unavailableTours => ', unavailableTours)
      tours = [...availableTours, ...unavailableTours]
      return tours
    }
  },
  data () {
    return {
      optionsDropdown: [
        { label: 'Popularity', value: 'popular' },
        { label: 'Price high to low', value: 'highest' },
        { label: 'Price low to high', value: 'lowest' },
        { label: 'Rating', value: 'rating' }
      ],
      initialSorting: false,
      ArrowIcon: ArrowIcon,
      message: {
        display: false,
        text: `Your search did not match any tours. 
        Please broaden your search criteria. In the meantime, here are some featured tours:`
      }
    }
  },
  validate({ params }) {
    const city = params.city
    // return false if city url not ending with -tours
    return city.endsWith('-tours')
  },
  async fetch ({ store, params, query, redirect }) {
    const city = params.city
    try {
      await store.dispatch('getListContent', { city, startDate: null, endDate: null })
      store.commit('setInitialFilter', query)
    } catch (err) {
      console.error(`city: ${city} - city data req error:`, err.message)
      redirect('/not-found/')
    }
  },
  head () {
    const blacklist = ['paris-tours', 'bordeaux-tours']
    const robots = blacklist.includes(this.$route.params.city) ? 'noindex' : this.setRobotsTag()

    return {
      title: (this.cityData !== undefined && this.cityData !== null) ? this.cityData.cityListingMetaTitle : '',
      meta: [
        {
          hid: 'robots', 
          name: 'robots', 
          content: robots
        },
        {
          hid: 'og:title',
          name: 'og:title',
          property: 'og:title',
          content: (this.cityData !== undefined && this.cityData !== null) ? this.cityData.cityListingMetaTitle : ''
        },
        {
          hid: 'description',
          name: 'description',
          content: this.cityData !== undefined ? this.cityData.cityListingMetaDescription : ''
        },
        {
          hid: 'og:description',
          name: 'og:description',
          property: 'og:description',
          content: this.cityData !== undefined ? this.cityData.cityListingMetaDescription : ''
        },
        {
          hid: 'og:type',
          name: 'og:type',
          property: "og:type",
          content : 'article'
        },
        {
          hid: 'og:url',
          name: 'og:url',
          property: 'og:url',
          content: this.getMetaURL()
        },
        {
          hid: 'og:image',
          name: 'og:image',
          property: 'og:image',
          content: this.returnMetaImage()
        },
        {
          property: 'twitter:card',
          content: 'summary_large_image'
        },
        {
          property: 'twitter:site',
          content: '@walks'
        },
        {
          property: 'twitter:title',
          content: (this.cityData !== undefined && this.cityData !== null) ? this.cityData.cityListingMetaTitle : ''
        },
        {
          property: 'twitter:card',
          content: 'summary_large_image'
        },
        {
          property: 'twitter:description',
          content: this.cityData !== undefined ? this.cityData.cityListingMetaDescription : ''
        },
        {
          property: 'twitter:image',
          content: this.returnMetaImage()
        },
        {
          hid: 'fb:app_id',
          name: 'fb:app_id',
          content: 1989124628036695
        }
      ],
      link: [
        { 'rel': 'canonical',
          'href': this.getMetaURL()
        },
        {
          rel: 'preload',
          as: 'image',
          href: this.getContentfulImgURL({ url: this.cityData.cityBannerImage }),
          importance: 'high'
        }
      ]
    }
  },
  mounted () {
    this.$store.commit('setSorter')
    this.$store.dispatch('getToursUnavailabilities', { city: this.$route.params.city })
    this.$store.dispatch('getCityPageReviewStat', { city: this.$route.params.city })
    this.$store.dispatch('getCityToursPrice', { city: this.$route.params.city })
  },
  methods: {
    returnMetaImage () {
      return 'https:' + this.cityData.cityBannerImage
    },
    returnMicroData () {
      return this.microData
    },
    getCityName () {
      let routerName = this.$route.params.city
      let array = routerName.split('-')
      let cityName = ''
      for (let index in array) {
        cityName += array[index].substr(0, 1).toUpperCase() + array[index].substr(1) + ' '
      }
      return cityName
    },
    getMetaURL () {
      let trailingSlash = ''
      if (!this.$route.path.match(/\/$/)) {
        trailingSlash = '/'
      }
      return 'https://www.walksofitaly.com' + this.$route.path + trailingSlash
    },
    dropdownChange (selected) {
      this.$store.commit('setSorter', selected)
    },
    getPopularity (tour) {
      return tour.reviewStatus?.feedbackCount || 0
    }
  }
}
</script>
<style lang="scss" scoped>
.city-tour-page {
  display: flex;
  flex-direction: column;
  align-items: center;
  .city-container {
    width: 1300px;
    padding: 30px 0;
    display: flex;
    align-items: flex-start;
    z-index: 2;
    flex-direction: column;

    .top-func {
      width: 100%;
      margin-bottom: 8px;
      .mobile-top {
        display: none;
      }
      .desktop-top {
        // if desktop, display TourFilter
        display: block;
        width: 100%;
        .first-line {
          margin-bottom: 24px;
          display: flex;
          justify-content: space-between;
          .dropdown {
            display: flex;
            justify-content: flex-end;
          }
          h2.header-2 {
            @include woi-header-4;
            color: $woi-red-main;
          }
        }
      }
    }
    .city-section {
      margin-top: 48px;
    }
  }

  // select {
  //   text-transform: uppercase;
  // }
  @include max-width($limit-width) {
    .city-container {
      width: Min(1200px, calc(100vw - 48px));
    }
  }

  @include max-width ($tablet) {
    .city-container {
      width: calc(100vw - 48px);
      .city-keyword {
        margin-bottom: 32px;
      }
      .top-func {   
        margin-bottom: 0; 
        .desktop-top {
          display: none;
        }  

        .mobile-top {
          display: flex;
          flex-direction: column;
          .header-2 {
            @include header-4;
            color: $woi-red-main;
          }
          > div {
            display: flex;
            justify-content: space-between;
            align-items: center;
          }
        }
      }
    }
  }

  @include max-width($mobile-extra) {
    .city-container {
      width: calc(100vw - 36px);
      .top-func {   
        .mobile-top {
          .header-2 {
            @include header-5;
          }
        }
      }
      .city-section {
        margin-top: 32px;
      }
    }
  }

  @include max-width($mobile-l) {
    .city-container {
      width: $mobile-width;
    }
  }
}
</style>
